import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Loader2, Target, MapPin, Users, CheckCircle, Crosshair } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { useOnboardingSession, useSaveOnboardingStep, useCompleteOnboarding } from './api/useOnboarding';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { getFeatureFlag } from '@/lib/featureFlags';

interface Coach {
  user_id: string;
  full_name: string;
  coach_bio: string | null;
}

const SKILL_LEVELS = [
  { value: 'beginner', label: 'Beginner', description: '20+ handicap - New to golf or just starting out' },
  { value: 'intermediate', label: 'Intermediate', description: '10-19 handicap - Play regularly, looking to improve' },
  { value: 'advanced', label: 'Advanced', description: '5-9 handicap - Experienced player seeking refinement' },
  { value: 'low_handicap', label: 'Low Handicap', description: '0-4 handicap - Skilled player fine-tuning technique' },
  { value: 'scratch_plus', label: 'Scratch Plus', description: '+2 to 2 handicap - Elite player seeking perfection' },
];

// US States and Canadian Provinces
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];


interface OnboardingWizardProps {
  isOpen: boolean;
  onComplete: () => void;
}

export function OnboardingWizard({ isOpen, onComplete }: OnboardingWizardProps) {
  const { data: session, isLoading: sessionLoading } = useOnboardingSession();
  const saveStep = useSaveOnboardingStep();
  const completeOnboarding = useCompleteOnboarding();

  const [currentStep, setCurrentStep] = useState(1);
  const [skillLevel, setSkillLevel] = useState<'beginner' | 'intermediate' | 'advanced' | 'low_handicap' | 'scratch_plus' | ''>('');
  const [handicap, setHandicap] = useState<string>('');
  const [primaryGoal, setPrimaryGoal] = useState('');
  const [region, setRegion] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [radius, setRadius] = useState(25);
  const [coachInterests, setCoachInterests] = useState<string[]>([]);
  
  const zipCodeSearchEnabled = getFeatureFlag('zip_code_radius_search');

  // Fetch coaches for step 4 (ZIP radius search for US, region filter for Canada/fallback)
  const { data: coaches, isLoading: coachesLoading } = useQuery({
    queryKey: ['coaches', 'directory', zipCode, radius, region],
    queryFn: async (): Promise<Coach[]> => {
      if (zipCode && zipCodeSearchEnabled) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) throw new Error('Not authenticated');

          const response = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/lookup-coach-radius`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${session.access_token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ zip: zipCode, radius }),
            }
          );

          if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Failed to lookup coaches' }));
            // Fallback to state-based search if ZIP lookup fails
            console.warn('[OnboardingWizard] ZIP radius search failed, falling back to state:', error);
            if (region) {
              const { data, error: queryError } = await supabase
                .from('coach_directory_view')
                .select('user_id, full_name, coach_bio')
                .eq('region', region)
                .limit(20);
              if (queryError) throw queryError;
              return data || [];
            }
            return [];
          }

          const result = await response.json();
          return (result.coaches || []).map((c: any) => ({
            user_id: c.user_id,
            full_name: c.full_name,
            coach_bio: c.coach_bio,
          }));
        } catch (error) {
          console.error('[OnboardingWizard] ZIP radius search error:', error);
          // Fallback to state-based search
          if (region) {
            const { data, error: queryError } = await supabase
              .from('coach_directory_view')
              .select('user_id, full_name, coach_bio')
              .eq('region', region)
              .limit(20);
            if (queryError) throw queryError;
            return data || [];
          }
          return [];
        }
      }

      // No ZIP code: use state-based filtering
      let query = supabase
        .from('coach_directory_view')
        .select('user_id, full_name, coach_bio')
        .limit(20);

      if (region) {
        query = query.eq('region', region);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: currentStep === 4,
  });

  // Load session data when available
  useEffect(() => {
    if (session && !session.completed) {
      setCurrentStep(session.step);
      const data = session.data || {};
      if (data.skill_level) setSkillLevel(data.skill_level);
      if (data.handicap !== undefined) setHandicap(String(data.handicap || ''));
      if (data.primary_goal) setPrimaryGoal(data.primary_goal);
      if (data.region) setRegion(data.region);
      if (data.zip_code) setZipCode(data.zip_code);
      if (data.radius) setRadius(data.radius);
      if (data.coach_interests) setCoachInterests(data.coach_interests);
    }
  }, [session]);

  const handleNext = async () => {
    if (currentStep < 5) {
      // Save current step data
      const stepData: Record<string, any> = {};
      if (currentStep === 1) {
        stepData.skill_level = skillLevel;
        stepData.handicap = handicap ? parseFloat(handicap) : null;
      }
      if (currentStep === 2) stepData.primary_goal = primaryGoal;
      if (currentStep === 3) {
        stepData.region = region;
        stepData.zip_code = zipCode;
        stepData.radius = radius;
      }
      if (currentStep === 4) stepData.coach_interests = coachInterests;

      try {
        await saveStep.mutateAsync({
          step: currentStep + 1,
          data: stepData,
        });
        setCurrentStep(currentStep + 1);
      } catch (error) {
        console.error('Failed to save step:', error);
        // Still advance step even if save fails (user can continue)
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    if (!skillLevel || !primaryGoal || !region) {
      return;
    }

    try {
      await completeOnboarding.mutateAsync({
        skill_level: skillLevel as 'beginner' | 'intermediate' | 'advanced' | 'low_handicap' | 'scratch_plus',
        handicap: handicap ? parseFloat(handicap) : null,
        primary_goal: primaryGoal,
        region,
        zip_code: zipCode || undefined,
        radius: zipCode ? radius : undefined,
        country: 'US',
        coach_interests: coachInterests,
      });
      onComplete();
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return !!skillLevel;
      case 2:
        return primaryGoal.trim().length > 0;
      case 3:
        return !!region;
      case 4:
        return true; // Coach selection is optional
      case 5:
        return true;
      default:
        return false;
    }
  };

  const STEP_META = [
    { label: 'Skill', icon: Target },
    { label: 'Goal', icon: Crosshair },
    { label: 'Location', icon: MapPin },
    { label: 'Coaches', icon: Users },
    { label: 'Review', icon: CheckCircle },
  ];

  const stepVariants = {
    enter: { opacity: 0, x: 30 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -30 },
  };

  if (sessionLoading) {
    return (
      <Dialog open={isOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (session?.completed) {
    return null; // Don't show wizard if already completed
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onComplete(); }}>
      <DialogContent className="sm:max-w-[600px] flex flex-col h-full sm:h-auto">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Welcome to No Doubles!</DialogTitle>
          <DialogDescription>
            Let's get you set up. This will only take a minute.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-shrink-0 py-4 sm:py-6">
            {/* Progress indicator with labels */}
            <div className="flex items-center justify-between px-2 sm:px-0">
              {STEP_META.map((meta, idx) => {
                const step = idx + 1;
                const Icon = meta.icon;
                return (
                  <React.Fragment key={step}>
                    <div className="flex flex-col items-center gap-1">
                      <div
                        className={`flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-full border-2 transition-colors duration-200 ${
                          step <= currentStep
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border bg-card text-muted-foreground'
                        }`}
                      >
                        <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                      </div>
                      <span className={`text-[10px] sm:text-xs font-medium ${step <= currentStep ? 'text-primary' : 'text-muted-foreground'}`}>
                        {meta.label}
                      </span>
                    </div>
                    {step < 5 && (
                      <div
                        className={`h-0.5 flex-1 mx-1 sm:mx-2 transition-colors duration-200 ${
                          step < currentStep ? 'bg-primary' : 'bg-border'
                        }`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            {/* Linear progress bar */}
            <div className="mt-3 h-1 w-full rounded-full bg-border">
              <div
                className="h-1 rounded-full bg-primary transition-all duration-300"
                style={{ width: `${(currentStep / 5) * 100}%` }}
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-1 sm:px-0">
            <AnimatePresence mode="wait">
            {/* Step 1: Skill Level */}
            {currentStep === 1 && (
              <motion.div key="step-1" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.2 }}>
              <div className="space-y-4 sm:space-y-6">
                <Label className="text-lg sm:text-xl font-semibold">What's your skill level?</Label>
                <RadioGroup value={skillLevel} onValueChange={(value) => setSkillLevel(value as any)}>
                  {SKILL_LEVELS.map((level) => (
                    <div key={level.value} className="flex items-start space-x-3 rounded-lg border p-3 sm:p-4 hover:bg-accent min-h-[44px] touch-manipulation">
                      <RadioGroupItem value={level.value} id={level.value} className="mt-1 flex-shrink-0" />
                      <Label htmlFor={level.value} className="flex-1 cursor-pointer">
                        <div className="font-medium text-sm sm:text-base">{level.label}</div>
                        <div className="text-xs sm:text-sm text-muted-foreground mt-1">{level.description}</div>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
                
                {/* Optional Handicap Field */}
                <div className="space-y-2 pt-2 border-t">
                  <Label className="text-sm font-medium">Handicap (Optional)</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 12"
                    value={handicap}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^-?\d*\.?\d*$/.test(value)) {
                        setHandicap(value);
                      }
                    }}
                    min="-10"
                    max="36"
                    step="0.1"
                    className="w-full h-11 sm:h-10"
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter your current handicap if you know it. This helps personalize your coaching.
                  </p>
                </div>
              </div>
              </motion.div>
            )}

            {/* Step 2: Primary Goal */}
            {currentStep === 2 && (
              <motion.div key="step-2" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.2 }}>
              <div className="space-y-4 sm:space-y-6">
                <Label className="text-lg sm:text-xl font-semibold">What's your primary goal?</Label>
                <Input
                  placeholder="e.g., Fix my slice, Lower my score, Improve consistency"
                  value={primaryGoal}
                  onChange={(e) => setPrimaryGoal(e.target.value)}
                  maxLength={100}
                  className="h-11 sm:h-10"
                />
                <p className="text-sm text-muted-foreground">
                  Tell us what you want to work on most
                </p>
              </div>
              </motion.div>
            )}

            {/* Step 3: Location */}
            {currentStep === 3 && (
              <motion.div key="step-3" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.2 }}>
              <div className="space-y-4 sm:space-y-6">
                <Label className="text-lg sm:text-xl font-semibold">Where are you located?</Label>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">State</Label>
                  <Select value={region} onValueChange={setRegion}>
                    <SelectTrigger className="h-11 sm:h-10">
                      <SelectValue placeholder="Select your state" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {US_STATES.map((state) => (
                        <SelectItem key={`US - ${state}`} value={`US - ${state}`}>
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {zipCodeSearchEnabled && (
                  <div className="space-y-4 pt-2 border-t">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">ZIP Code (optional)</Label>
                      <Input
                        type="text"
                        placeholder="12345"
                        value={zipCode}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                          setZipCode(value);
                        }}
                        maxLength={5}
                        className="w-full h-11 sm:h-10"
                      />
                      {zipCode.length > 0 && zipCode.length < 5 && (
                        <p className="text-xs text-muted-foreground">Enter 5-digit ZIP code</p>
                      )}
                    </div>

                    {zipCode.length === 5 && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium">Search Radius</Label>
                          <span className="text-sm text-muted-foreground">{radius} miles</span>
                        </div>
                        <Slider
                          value={[radius]}
                          onValueChange={(values) => setRadius(values[0])}
                          min={10}
                          max={100}
                          step={5}
                          className="w-full"
                        />
                        <p className="text-xs text-muted-foreground">
                          Find coaches within {radius} miles of your ZIP code
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <p className="text-sm text-muted-foreground">
                  This helps us match you with local coaches and groups in your area
                </p>
              </div>
              </motion.div>
            )}

            {/* Step 4: Coach Interest */}
            {currentStep === 4 && (
              <motion.div key="step-4" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.2 }}>
              <div className="space-y-4 sm:space-y-6">
                <Label className="text-lg sm:text-xl font-semibold">Interested in any coaches? (Optional)</Label>
                <p className="text-sm text-muted-foreground">
                  {zipCode ? (
                    <>Finding coaches within {radius} miles of ZIP {zipCode}...</>
                  ) : (
                    <>Select coaches you'd like to follow. You can always add more later.</>
                  )}
                </p>
                {coachesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : (
                  <div className="max-h-64 sm:max-h-80 space-y-2 overflow-y-auto">
                    {coaches && coaches.length > 0 ? (
                      coaches.map((coach) => (
                        <div
                          key={coach.user_id}
                          className="flex items-start space-x-3 rounded-lg border p-3 sm:p-4 hover:bg-accent min-h-[44px] touch-manipulation"
                        >
                          <Checkbox
                            checked={coachInterests.includes(coach.user_id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setCoachInterests([...coachInterests, coach.user_id]);
                              } else {
                                setCoachInterests(coachInterests.filter((id) => id !== coach.user_id));
                              }
                            }}
                            className="mt-1 flex-shrink-0"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-sm sm:text-base">{coach.full_name}</div>
                            {coach.coach_bio && (
                              <div className="text-xs sm:text-sm text-muted-foreground line-clamp-2 mt-1">
                                {coach.coach_bio}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-sm text-muted-foreground">
                          {zipCode ? (
                            <>No coaches found within {radius} miles of ZIP {zipCode}.</>
                          ) : (
                            <>No coaches available at this time.</>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              </motion.div>
            )}

            {/* Step 5: Confirmation */}
            {currentStep === 5 && (
              <motion.div key="step-5" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.2 }}>
              <div className="space-y-4 sm:space-y-6">
                <Label className="text-lg sm:text-xl font-semibold">Review your information</Label>
                <div className="space-y-3 rounded-lg border p-4 sm:p-6">
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Skill Level:</span>
                    <span className="ml-2 capitalize">{skillLevel.replace(/_/g, ' ')}</span>
                  </div>
                  {handicap && (
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">Handicap:</span>
                      <span className="ml-2">{parseFloat(handicap) >= 0 ? '+' : ''}{handicap}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Primary Goal:</span>
                    <span className="ml-2">{primaryGoal}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Location:</span>
                    <span className="ml-2">
                      {region}
                      {zipCode && <> &middot; ZIP {zipCode} ({radius} mi)</>}
                    </span>
                  </div>
                  {coachInterests.length > 0 && (
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">Coaches:</span>
                      <span className="ml-2">{coachInterests.length} selected</span>
                    </div>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  Ready to get started? We'll create a welcome post and match you with groups!
                </p>
              </div>
              </motion.div>
            )}
            </AnimatePresence>
          </div>

        </div>

        {/* Navigation buttons - Fixed at bottom on mobile */}
        <div className="flex-shrink-0 flex justify-between gap-3 pt-4 sm:pt-6 border-t bg-background">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1 || completeOnboarding.isPending}
            className="h-11 sm:h-10 min-w-[100px] touch-manipulation"
          >
            <ChevronLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          {currentStep < 5 ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed() || saveStep.isPending}
              className="bg-primary hover:bg-primary-hover h-11 sm:h-10 min-w-[100px] touch-manipulation text-primary-foreground"
            >
              Next
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={handleComplete}
              disabled={!canProceed() || completeOnboarding.isPending}
              className="bg-primary hover:bg-primary-hover h-11 sm:h-10 min-w-[120px] touch-manipulation text-primary-foreground"
            >
              {completeOnboarding.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Completing...
                </>
              ) : (
                'Complete Setup'
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

